import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { RefreshCcw, Maximize2, Home } from "lucide-react";
import { toast } from "sonner";
import { apiClient, getApiErrorMessage } from "../../lib/apiClient";
import { Skeleton } from "../../components/ui/skeleton";
import { ErrorState } from "../../components/shared/ErrorState";
import { Button } from "../../components/ui/button";
import { Dialog, DialogContent, DialogTitle } from "../../components/ui/dialog";

export default function CustomerQrWallet() {
  const navigate = useNavigate();
  const [state, setState] = useState({ loading: true, error: null, me: null });
  const [qr, setQr] = useState({ loading: false, qr_token: null, active: false });
  const [busy, setBusy] = useState(false);
  const [expanded, setExpanded] = useState(false);

  const load = () => {
    setState((s) => ({ ...s, loading: true, error: null }));
    apiClient
      .get("/customer/me")
      .then(({ data }) => {
        setState({ loading: false, error: null, me: data });
        if (data.type === "1") {
          apiClient.get("/customer/qr").then(({ data: q }) => setQr({ loading: false, ...q }));
        }
      })
      .catch((error) => setState({ loading: false, error: getApiErrorMessage(error, "Could not load your account."), me: null }));
  };

  useEffect(() => { load(); }, []);

  const regenerateQr = async () => {
    if (!window.confirm("Your current QR code will stop working immediately. Continue?")) return;
    setBusy(true);
    try {
      const { data } = await apiClient.post("/customer/qr/regenerate");
      setQr({ loading: false, ...data });
      toast.success("New QR code generated.");
    } catch (error) {
      toast.error(getApiErrorMessage(error, "Could not generate a new QR code."));
    } finally {
      setBusy(false);
    }
  };

  if (state.loading) {
    return (
      <div data-testid="customer-qr-page">
        <Skeleton className="h-96 w-full rounded-3xl" />
      </div>
    );
  }

  if (state.error) {
    return <ErrorState message={state.error} onRetry={load} testId="customer-qr-error" />;
  }

  const me = state.me;

  if (!me?.has_subscription) {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-surface py-14 text-center" data-testid="customer-qr-no-subscription">
        <p className="text-sm text-text-muted">Get a plan to receive your pickup QR code.</p>
        <Button className="mt-4 rounded-xl" onClick={() => navigate("/customer/register")} data-testid="customer-qr-get-plan-button">
          Get a plan
        </Button>
      </div>
    );
  }

  if (me.type !== "1") {
    return (
      <div className="rounded-2xl border border-dashed border-border bg-surface py-14 text-center" data-testid="customer-qr-not-applicable">
        <p className="text-sm text-text-muted">Pickup QR codes are only for pickup customers.</p>
        <p className="mt-1 text-xs text-text-muted">You're on a home delivery plan — check My Tiffins for delivery status.</p>
        <Button className="mt-4 rounded-xl" variant="outline" onClick={() => navigate("/customer/history")} data-testid="customer-qr-go-to-history-button">
          <Home size={14} strokeWidth={1.5} className="mr-1.5" /> Go to My Tiffins
        </Button>
      </div>
    );
  }

  return (
    <div data-testid="customer-qr-page">
      <div className="mx-auto max-w-sm space-y-4 rounded-3xl border border-border bg-surface p-6 text-center shadow-xl">
        <div>
          <p className="text-sm font-semibold text-text-main">{me.plan_name}</p>
          <p className="text-xs text-text-muted">Customer ID #{me.customer_id}</p>
        </div>

        {qr.qr_token ? (
          <button
            type="button"
            onClick={() => setExpanded(true)}
            className={`group relative mx-auto inline-block rounded-2xl border-2 border-accent bg-white p-4 transition-all active:scale-95 ${!qr.active ? "opacity-30" : ""}`}
            data-testid="customer-qr-expand-button"
          >
            <QRCodeSVG value={qr.qr_token} size={200} data-testid="customer-qr-code" />
            <span className="absolute inset-0 hidden items-center justify-center rounded-2xl bg-black/40 group-hover:flex">
              <Maximize2 size={22} className="text-white" />
            </span>
          </button>
        ) : (
          <Skeleton className="mx-auto h-[220px] w-[220px] rounded-2xl" />
        )}

        <span
          className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-medium ${qr.active ? "bg-success-bg text-success" : "bg-danger-bg text-danger"}`}
          data-testid="customer-qr-status-badge"
        >
          {qr.active ? "Active for Pickup" : "Inactive — Renewal Required"}
        </span>

        <p className="text-xs text-text-muted">Scan this code at the counter during meal hours.</p>

        <button
          type="button" onClick={regenerateQr} disabled={busy}
          className="flex w-full items-center justify-center gap-1.5 rounded-xl border border-border py-3 text-xs font-medium text-text-muted transition-all active:scale-95 disabled:opacity-60"
          data-testid="customer-qr-regenerate-button"
        >
          <RefreshCcw size={13} strokeWidth={1.5} /> Get a new QR code
        </button>
      </div>

      <Dialog open={expanded} onOpenChange={setExpanded}>
        <DialogContent className="flex flex-col items-center justify-center gap-4 border-none bg-white p-8 sm:max-w-md" data-testid="customer-qr-fullscreen-dialog">
          <DialogTitle className="sr-only">Pickup QR Code</DialogTitle>
          {qr.qr_token && <QRCodeSVG value={qr.qr_token} size={280} data-testid="customer-qr-code-fullscreen" />}
          <p className="text-center text-sm text-text-muted">Scan this code at the counter during meal hours.</p>
        </DialogContent>
      </Dialog>
    </div>
  );
}
